/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/shipping-and-fulfillment/fulfillment
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import { graphqlMoneyBagFieldToRest } from "./constants";
import {
  ShopifyFulfillmentKey,
  ShopifyFulfillmentLineItemKey,
  ShopifyFulfillmentLineItemToFulfillmentKey,
  ShopifyFulfillmentLineItemToOrderLineItemKey,
  ShopifyFulfillmentLineItemToReverseFulfillmentOrderLineItemsKey,
  ShopifyFulfillmentLineItemToShopKey,
  ShopifyOrderLineItemKey,
  ShopifyReverseFulfillmentOrderLineItemKey,
  ShopifyReverseFulfillmentOrderLineItemToFulfillmentLineItemKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyFulfillmentLineItemKey,
  "2022-01": {
    name: "Shopify Fulfillment Line Item",
    fields: [
      {
        name: "Discounted Total Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_discount_set" } },
      },
      {
        name: "Fulfillment",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentLineItemToFulfillmentKey,
        configuration: {
          relatedModelKey: ShopifyFulfillmentKey,
        },
      },
      {
        name: "Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentLineItemToOrderLineItemKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "lineItem", graphQLSelection: "id" } },
        configuration: {
          relatedModelKey: ShopifyOrderLineItemKey,
        },
      },
      {
        name: "Original Total Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "price_set" } },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentLineItemToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_list", resource: "order" },
  },
  "2025-04": {
    fields: [
      {
        name: "Discounted Total Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "total_discount_set",
            graphQLFieldKey: "discountedTotalSet",
            graphQLSelection: graphqlMoneyBagFieldToRest,
            availableInWebhook: ["fulfillments/create", "fulfillments/update", "orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Fulfillment",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentLineItemToFulfillmentKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "fulfillmentId", skipGraphQLSelection: true } },
        configuration: {
          relatedModelKey: ShopifyFulfillmentKey,
        },
      },
      {
        name: "Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentLineItemToOrderLineItemKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "lineItem",
            graphQLSelection: "id",
            availableInWebhook: ["fulfillments/create", "fulfillments/update", "orders/create", "orders/updated"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyOrderLineItemKey,
        },
      },
      {
        name: "Reverse Fulfillment Order Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyFulfillmentLineItemToReverseFulfillmentOrderLineItemsKey,
        configuration: {
          relatedModelKey: ShopifyReverseFulfillmentOrderLineItemKey,
          inverseFieldKey: ShopifyReverseFulfillmentOrderLineItemToFulfillmentLineItemKey,
        },
      },
      {
        name: "Original Total Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "price_set",
            graphQLFieldKey: "originalTotalSet",
            graphQLSelection: graphqlMoneyBagFieldToRest,
            availableInWebhook: ["fulfillments/create", "fulfillments/update", "orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "quantity",
            graphQLFieldKey: "quantity",
            availableInWebhook: ["fulfillments/create", "fulfillments/update", "orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Admin GraphQL API ID",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "id", graphQLOnlyField: true, shopifyFieldKey: "__gadget_graphql_admin_graphql_api_id" },
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "order.fulfillments",
      resource: "fulfillment.fulfillmentLineItems",
    },
  },
};

export const ShopifyFulfillmentLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
